import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { useMediaQuery } from 'usehooks-ts';
import {
  FormTemplateQuestionFieldOptionsValidityHintEnum,
  Task,
  TaskCompletionScopeNameEnum,
  TaskScopeInputObject,
} from '@lib/graphql-common';
import Modal from '@lib/components/Modal/Modal';
import { ModalMode } from '@lib/components/Modal/enums';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import IconSurface from '@lib/components/IconSurface/IconSurface';
import { Colors } from '@lib/enums/colors';
import { BELOW_MD1_QUERY } from '@lib/assets/styles/breakpoints';
import {
  TableMultiselectHandle,
  tableMultiselectModalContentClassName,
} from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import useSelectedFormFieldIds from '@lib/components/ModalTableMultiselect/hooks/useSelectedFormFieldIds';
import { Values } from '@lib/interfaces/form';
import TasksTableMultiselect from '../TasksTableMultiselect/TasksTableMultiselect';
import { TasksTableMultiselectFilterLoadOptions } from '../TasksTableMultiselect/types';
import TaskCompletionsTableMultiselect from '../TaskCompletionsTableMultiselect/TaskCompletionsTableMultiselect';
import { TaskCompletionsModalUrls } from '../TaskCompletionsModal/types';

export type TaskAndCompletionsModalStep = 'task' | 'completions';

type StagedTask = { values: string[]; selectedItems?: Task[] };

export type TaskAndCompletionsModalProps = {
  // App-specific: the Task step's filter search lists and the Completions
  // step's completion-preview routes.
  taskFilterLoadOptions: TasksTableMultiselectFilterLoadOptions;
  completionUrls: TaskCompletionsModalUrls;
  // See `TaskCompletionsTableMultiselect`'s `hideCompletionPreview`.
  hideCompletionPreview?: boolean;
  isOpen: boolean;
  step: TaskAndCompletionsModalStep;
  onStepChange: (step: TaskAndCompletionsModalStep) => void;
  onClose: () => void;
  formRef: FormRefType;
  taskFieldName: string;
  completionsFieldName: string;
  onTaskSelect: (values: string[], selectedItems?: Task[]) => void;
  onCompletionsSelect: (values: string[]) => void;
  completionsGroupKey?: string;
  taskFilterOverrides?: Values;
  taskDisabledFilterFields?: string[];
  taskScope?: TaskScopeInputObject;
  completionsScope?: TaskCompletionScopeNameEnum;
  completionsExcludedStatusFilterOptions?: FormTemplateQuestionFieldOptionsValidityHintEnum[];
};

/**
 * One modal, two steps: pick the related Task, then pick that task's
 * Completions. Both steps render the shared pickers in `inline` mode — the
 * picker body only — so the surrounding `Modal`, its caption and its
 * Cancel/Confirm buttons stay put across the step change instead of one modal
 * closing and another opening. Confirmation is driven through each step's
 * `TableMultiselectHandle`.
 *
 * The wizard writes nothing to the host form until the final confirmation:
 * step 1's "Next" only stages the picked Task locally, and both fields are
 * committed together when step 2 is confirmed. Cancelling — on either step —
 * therefore leaves the form exactly as it was.
 *
 * Opening straight on `step: 'completions'` (re-clicking the Completions field
 * once a Task is already set) skips step 1; Back (footer button, or the header
 * arrow on small screens) is then hidden,
 * since there is no earlier step to return to in that flow.
 */
function TaskAndCompletionsModal({
  taskFilterLoadOptions,
  completionUrls,
  hideCompletionPreview,
  isOpen,
  step,
  onStepChange,
  onClose,
  formRef,
  taskFieldName,
  completionsFieldName,
  onTaskSelect,
  onCompletionsSelect,
  completionsGroupKey,
  taskFilterOverrides,
  taskDisabledFilterFields,
  taskScope,
  completionsScope,
  completionsExcludedStatusFilterOptions,
}: TaskAndCompletionsModalProps) {
  const { t } = useTranslation();
  const taskStepRef = useRef<TableMultiselectHandle>(null);
  const completionsStepRef = useRef<TableMultiselectHandle>(null);

  // The step this run of the wizard started on — `step` itself moves as the
  // user advances, so it can't answer "was there a step before this one?".
  // Captured while rendering the open transition rather than in an effect: an
  // effect lands after the first paint, so reopening straight on 'completions'
  // would briefly show the Back control left over from the previous run.
  const prevIsOpenRef = useRef(isOpen);
  const [openedAtStep, setOpenedAtStep] = useState(step);
  // Step 1's pick, held back from the form until step 2 is confirmed. `null`
  // means step 1 wasn't part of this run (opened straight on 'completions'),
  // so there is no Task to commit alongside the Completions.
  const [stagedTask, setStagedTask] = useState<StagedTask | null>(null);
  if (prevIsOpenRef.current !== isOpen) {
    prevIsOpenRef.current = isOpen;
    if (isOpen) {
      setOpenedAtStep(step);
      setStagedTask(null);
    }
  }

  const isTaskStep = step === 'task';
  const canGoBack = !isTaskStep && openedAtStep === 'task';
  // Matches `ActionsRow`'s breakpoint for stacking its actions.
  const isSmallScreen = useMediaQuery(BELOW_MD1_QUERY);

  // Staging a *different* Task invalidates the Completions the form still
  // holds for the previous one. They'd be invisible on step 2 — both of its
  // queries are scoped to the new `groupKey`, so the rows never come back and
  // can't be unchecked — yet they'd still ride along into
  // `onCompletionsSelect`. Re-picking the same Task is not a change, so the
  // existing picks stay.
  const [committedTaskId] = useSelectedFormFieldIds(formRef, taskFieldName);
  // The form's Task field holds a `{ value: groupKey, label: task name }`
  // option — its label names the task in the completion preview's header.
  const committedTaskOption = _get(formRef.current?.values, taskFieldName) as
    { label?: string } | null | undefined;
  const isTaskChanged =
    !!stagedTask && stagedTask.values[0] !== committedTaskId;

  // Commit order matters: the form's Task field resets Completions on change
  // (see `getInterventionFormFields`), so writing the Task second would wipe
  // the Completions we just wrote. An unchanged Task isn't re-committed: when
  // it sits off the picker's loaded page its row (hence `selectedItems`) is
  // unknown, and re-committing would clear the form's Task.
  const handleCompletionsSelect = (values: string[]) => {
    if (stagedTask && isTaskChanged) {
      onTaskSelect(stagedTask.values, stagedTask.selectedItems);
    }
    onCompletionsSelect(values);
  };

  const goBack = canGoBack ? () => onStepChange('task') : undefined;

  const cancelButton = (
    <Button
      key="cancel"
      buttonText={t('cancel')}
      buttonType={ButtonTypes.secondaryOutlined}
      leftIcon="close"
      onClick={onClose}
      fullWidth
    />
  );
  const submitButton = (
    <Button
      key="submit"
      buttonText={isTaskStep ? t('next') : t('confirm-selection')}
      buttonType={ButtonTypes.primaryFilled}
      leftIcon={isTaskStep ? undefined : 'check'}
      rightIcon={isTaskStep ? 'arrow_forward' : undefined}
      onClick={() =>
        (isTaskStep
          ? taskStepRef.current
          : completionsStepRef.current
        )?.confirmSelection()
      }
      fullWidth
    />
  );

  // Small screens stack the actions, so Back stays as the header arrow and the
  // footer keeps just Cancel + submit. Wider screens use the create-entity
  // form layout: Cancel on the left, Back + submit on the right, with empty
  // `div`s holding the four-column grid when a slot has no button.
  const actions = (
    <ActionsRow
      actions={
        isSmallScreen
          ? [cancelButton, submitButton]
          : [
              cancelButton,
              <div key="spacer" />,
              goBack ? (
                <Button
                  key="back"
                  buttonText={t('back')}
                  buttonType={ButtonTypes.primaryOutlined}
                  leftIcon="arrow_back"
                  onClick={goBack}
                  fullWidth
                />
              ) : (
                <div key="back" />
              ),
              submitButton,
            ]
      }
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onBack={isSmallScreen ? goBack : undefined}
      caption={isTaskStep ? t('task-select') : t('completions-multiselect')}
      renderAfterCaption={
        <Tooltip
          body={t(
            isTaskStep
              ? 'choose-task-for-completions-hint'
              : 'select-completions-of-selected-task-hint',
          )}
        >
          <IconSurface
            icon="info"
            symbolsOutlined
            color={Colors.Gray}
            size="sm"
          />
        </Tooltip>
      }
      mode={ModalMode.FullScreen}
      modalContentClassName={tableMultiselectModalContentClassName}
      fixedActions={actions}
    >
      {isTaskStep ? (
        <TasksTableMultiselect
          filterLoadOptions={taskFilterLoadOptions}
          inline
          ref={taskStepRef}
          isOpen={isOpen}
          onClose={onClose}
          onSelect={(values, selectedItems) =>
            setStagedTask({ values, selectedItems })
          }
          // Confirming a Task advances the wizard rather than closing it.
          onSelectionConfirmed={() => onStepChange('completions')}
          selectedIdsOverride={stagedTask?.values}
          formRef={formRef}
          fieldName={taskFieldName}
          radioSelect
          selectionLimit={1}
          filterOverrides={taskFilterOverrides}
          disabledFilterFields={taskDisabledFilterFields}
          scope={taskScope}
        />
      ) : (
        <TaskCompletionsTableMultiselect
          urls={completionUrls}
          inline
          ref={completionsStepRef}
          isOpen={isOpen}
          onClose={onClose}
          onSelect={handleCompletionsSelect}
          onSelectionConfirmed={onClose}
          formRef={formRef}
          fieldName={completionsFieldName}
          selectedIdsOverride={isTaskChanged ? [] : undefined}
          groupKey={stagedTask ? stagedTask.values[0] : completionsGroupKey}
          taskName={
            isTaskChanged
              ? stagedTask?.selectedItems?.[0]?.name
              : committedTaskOption?.label
          }
          // Step 1's row already carries Site / Site Area / Asset; otherwise
          // the picker fetches the task by groupKey for its header.
          task={stagedTask?.selectedItems?.[0]}
          scope={completionsScope}
          excludedStatusFilterOptions={completionsExcludedStatusFilterOptions}
          restrictToUnsolved
          hideCompletionPreview={hideCompletionPreview}
        />
      )}
    </Modal>
  );
}

export default TaskAndCompletionsModal;
